var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { getHighestUserRole, USER_ROLE_ATTRIBUTES } from 'lib-shared-user-role';
import { destroy } from 'redux-form';
import { notificationActions } from 'components/notifications/actions/notification';
import { createCompany } from 'components/shared/companyModal/api/company';
import { getRequestID } from 'components/shared/util/request';
import { dataUriToFile } from 'utils/file/dataUriToFile';
import { pubSub } from 'utils/pubSub';
import { addUser, getPresigned, getUser, updateUser, uploadAvatar, validateEmail, validatePhoneNumber, } from './userModal.api';
export const userModalFormName = 'userModalForm';
const prefix = 'userModal';
export const userModalActions = {
    show: createAction(`${prefix}/show`),
    hide: createAction(`${prefix}/hide`),
    user: {
        get: createAsyncThunk(`${prefix}/user/get`, getUser),
        add: createAsyncThunk(`${prefix}/user/add`, (_a, _b) => __awaiter(void 0, void 0, void 0, function* () {
            var _c;
            var { avatar_url } = _a, user = __rest(_a, ["avatar_url"]);
            var dispatch = _b.dispatch, rejectWithValue = _b.rejectWithValue;
            try {
                let userWithAvatar = null;
                if (avatar_url) {
                    const file = yield dataUriToFile(avatar_url, 'avatar');
                    const presignedResponse = yield getPresigned({ file });
                    yield uploadAvatar({
                        file,
                        upload_url: presignedResponse.data.upload_url,
                    });
                    userWithAvatar = Object.assign(Object.assign({}, user), { avatar_url: presignedResponse.data.download_url });
                }
                const addUserResp = yield addUser(userWithAvatar ? userWithAvatar : user);
                const roleName = addUserResp.data.roles
                    ? USER_ROLE_ATTRIBUTES[getHighestUserRole(addUserResp.data.roles)]
                        .prettyName
                    : 'User';
                const notificationID = getRequestID();
                dispatch(userModalActions.hide());
                dispatch(destroy(userModalFormName));
                pubSub.publish('userModalUserAdded', user);
                dispatch(notificationActions.notificationAdd({
                    id: notificationID,
                    title: 'User saved',
                    text: `New ${roleName} added`,
                    status: 'success',
                    type: 'userCreated',
                    icon: 'done',
                    time: 5000,
                    rightAction: {
                        text: 'Dismiss',
                        action: () => {
                            dispatch(notificationActions.notificationRemoveById({
                                id: notificationID,
                            }));
                        },
                    },
                    leftAction: {
                        text: `Go to new user`,
                        action: {
                            navigate: `/contacts/user/${addUserResp.data.id}`,
                        },
                    },
                }));
                return addUserResp;
            }
            catch (error) {
                if (!error) {
                    dispatch(notificationActions.notificationAdd({
                        title: 'Error',
                        text: `An error occurred while adding the new user`,
                        status: 'danger',
                        time: 5000,
                        icon: 'exclamation',
                    }));
                }
                return rejectWithValue((_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.data);
            }
        })),
        update: createAsyncThunk(`${prefix}/user/update`, (user, { dispatch, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
            var _d, _e, _f;
            try {
                let userWithAvatar = null;
                if ((_d = user.patch.updates) === null || _d === void 0 ? void 0 : _d.avatar_url) {
                    const file = yield dataUriToFile((_e = user.patch.updates) === null || _e === void 0 ? void 0 : _e.avatar_url, 'avatar');
                    const presignedResponse = yield getPresigned({ file });
                    yield uploadAvatar({
                        file,
                        upload_url: presignedResponse.data.upload_url,
                    });
                    userWithAvatar = Object.assign(Object.assign({}, user), { patch: Object.assign(Object.assign({}, user.patch), { updates: Object.assign(Object.assign({}, user.patch.updates), { avatar_url: presignedResponse.data.download_url }) }) });
                }
                const updatedUser = yield updateUser(userWithAvatar ? userWithAvatar : user);
                pubSub.publish('userModalUserUpdated', updatedUser.data);
                dispatch(userModalActions.hide());
                dispatch(destroy(userModalFormName));
                dispatch(notificationActions.notificationAdd({
                    title: 'User updated',
                    text: `The user is updated`,
                    status: 'success',
                    time: 5000,
                    icon: 'done',
                }));
                return updatedUser;
            }
            catch (error) {
                if (!error) {
                    dispatch(notificationActions.notificationAdd({
                        title: 'Error',
                        text: `An error occurred while updating the user`,
                        status: 'danger',
                        time: 5000,
                        icon: 'exclamation',
                    }));
                }
                return rejectWithValue((_f = error === null || error === void 0 ? void 0 : error.response) === null || _f === void 0 ? void 0 : _f.data);
            }
        })),
    },
    validation: {
        email: createAsyncThunk(`${prefix}/validation/email`, (email, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield validateEmail(email);
            if (data.available) {
                return data;
            }
            return rejectWithValue(data);
        })),
        phoneNumber: createAsyncThunk(`${prefix}/validation/phone-number`, (phoneNumber, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield validatePhoneNumber(phoneNumber);
            if (data.available) {
                return data;
            }
            return rejectWithValue(data);
        })),
    },
    companies: {
        // TODO [TEAMS-6857]: When RTK Q is ready, `fulfilled` and `rejected`
        // should be removed and `get` should be converted to `createAsyncThunk`
        // and "companies select" in "userModal" should be paginated/infinite scrolled
        get: createAction(`${prefix}/companies/get`),
        fulfilled: createAction(`${prefix}/companies/fulfilled`),
        rejected: createAction(`${prefix}/companies/rejected`),
        add: createAsyncThunk(`${prefix}/addCompany`, (payload) => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield createCompany(payload);
            return { company: data };
        })),
    },
};
